'use client'

import Image from 'next/image'

import { useState, useEffect } from 'react'
import CompanyForm from './components/CompanyForm'
import RecoveryModal from './components/RecoveryModal'
import api from '../lib/api'
import { useRouter } from 'next/navigation'

import BenefitsSection from './components/BenefitsSection'
import ProcessSection from './components/ProcessSection'
import { FaqSection } from './components/FaqSection'
import TypewriterEffect from './components/TypewriterEffect'
import RiscosExamesSection from './components/RiscosExamesSection'
import ClinicasParceirasSection from './components/ClinicasParceirasSection'
import { trackingService } from '../lib/services/tracking.service'
import { useContactInfo } from '../lib/hooks/useContactInfo'

export default function Home() {
  const { contactInfo } = useContactInfo()
  const rawPhone = contactInfo?.fone || '+55-11-5178-5947'
  let cleanPhone = rawPhone.replace(/\D/g, '')
  if (cleanPhone.length >= 10 && cleanPhone.length <= 11 && !cleanPhone.startsWith('55')) {
    cleanPhone = '55' + cleanPhone
  }
  const whatsappUrl = `https://wa.me/${cleanPhone}`

  const [exames] = useState([
    'admissional',
    'demissional',
    'periódico',
    'mudança de função',
    'retorno ao trabalho'
  ])

  const router = useRouter()
  const [showRecoveryModal, setShowRecoveryModal] = useState(false)
  const [pendingOrder, setPendingOrder] = useState<any>(null)

  // Track page view
  useEffect(() => {
    trackingService.trackPageView('/');
    // NÃO verificar pedidos pendentes na página inicial - essa rota requer autenticação
    // A verificação deve ser feita apenas após o usuário fazer login/criar empresa
  }, [])

  const handleConfirmRecovery = async () => {
    if (!pendingOrder) return;
    try {
      const response = await api.get(`/pedidos/${pendingOrder.id}`);
      const pedido = response.data;

      // 1. Map CompanyData
      const companyData = {
        ocupation: pedido.empresa.cnpj && pedido.empresa.cnpj.length > 11 ? 'PJ' : 'PF',
        cnpj: pedido.empresa.cnpj || '',
        cpf: pedido.empresa.cnpj || '', // Assuming simplified logic where cnpj field holds identifier
        nomeEmpresa: pedido.empresa.razao_social,
        nomeEmpregador: pedido.empresa_solicitante?.nome || '',
        email: pedido.empresa_solicitante?.email || '',
        telefone: pedido.empresa.telefone || '',
        pcmso: '0', // Default as we might not have it in simple mapping
        nomeMedico: '',
        crmMedico: '',
        ufCRM: '',
        optante_simples_nacional: pedido.empresa.optante_simples_nacional || false,
      };
      localStorage.setItem('companyData', JSON.stringify(companyData));

      // 2. Map EmployeesData
      const employeesMap = new Map<number, any>();

      pedido.agendamentos.forEach((ag: any) => {
        if (!employeesMap.has(ag.colaborador_id)) {
          employeesMap.set(ag.colaborador_id, {
            id: ag.colaborador.id.toString(),
            nome: ag.colaborador.nome,
            cpf: ag.colaborador.cpf,
            // Assuming simplified mapping for resuming
            clinica: ag.clinica_id.toString(),
            clinicaNome: ag.clinica.nome,
            dataAgendamento: ag.data_agendamento.split('T')[0],
            examesSelecionados: [],
            variacao: ag.variacao_id ? ag.variacao_id.toString() : '1'
          });
        }
        const emp = employeesMap.get(ag.colaborador_id);
        emp.examesSelecionados.push(ag.exame.id);
      });

      const employeesData = Array.from(employeesMap.values());

      // Vincular colaboradores à empresa atual antes de salvar
      try {
        const empresaDataStr = localStorage.getItem('empresaData');
        if (empresaDataStr) {
          const empresaData = JSON.parse(empresaDataStr);
          const employeesDataWithCompany = employeesData.map((emp: any) => ({
            ...emp,
            empresa_id: empresaData.id,
            empresa_cnpj: empresaData.cnpj || empresaData.cpf,
          }));
          localStorage.setItem('employeesData', JSON.stringify(employeesDataWithCompany));
        } else {
          localStorage.setItem('employeesData', JSON.stringify(employeesData));
        }
      } catch (error) {
        console.warn('⚠️ Erro ao vincular empresa_id aos colaboradores:', error);
        localStorage.setItem('employeesData', JSON.stringify(employeesData));
      }

      // 3. Redirect
      router.push('/resumo');

    } catch (error) {
      console.error('Error recovering order:', error);
      alert('Erro ao recuperar o pedido. Por favor, comece novamente.');
      setShowRecoveryModal(false);
    }
  };

  const handleCancelRecovery = () => {
    setShowRecoveryModal(false);
    // Optional: Call API to ignore/cancel explicitely if needed
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'MedicalOrganization',
    name: 'SST Exames',
    url: 'https://sstexames.com.br',
    logo: 'https://sstexames.com.br/image/sst_logo.png',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: rawPhone,
      contactType: 'customer service',
      areaServed: 'BR',
      availableLanguage: 'Portuguese'
    },
    sameAs: [
      'https://www.instagram.com/sstexames/',
      'https://www.linkedin.com/company/sstexames/'
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      {/* Hero Section */}
      <section className="bg-white pt-20 pb-16 lg:pt-32 lg:pb-24 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="z-10 relative">
              <h1 className="text-4xl lg:text-5xl font-semibold text-gray-900 mb-6 leading-tight">
                Simplifique a Gestão de <span className="text-green-600">Exames Ocupacionais</span>
              </h1>
              <div className="text-xl lg:text-2xl text-gray-600 mb-8 font-light">
                Precisa agendar <span className="font-medium text-gray-900"><TypewriterEffect /></span>?
              </div>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-lg">
                O primeiro marketplace de clínicas e laboratórios ocupacionais do Brasil.
                Agende, pague e receba suas guias em minutos, sem burocracia.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="#formularioAgendamento"
                  className="bg-green-600 text-white px-8 py-4 rounded-lg hover:bg-green-500 transition-all transform hover:-translate-y-1 shadow-lg hover:shadow-xl text-center font-bold text-lg flex items-center justify-center gap-2"
                >
                  Começar Agora
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </a>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-gray-700 border-2 border-gray-200 px-8 py-4 rounded-lg hover:border-green-600 hover:text-green-600 transition-all text-center font-semibold text-lg flex items-center justify-center gap-2"
                >
                  Falar com Consultor
                </a>
              </div>

              <div className="mt-8">
                <p className="inline-block text-xs font-semibold uppercase tracking-wider text-gray-400 border border-gray-200 rounded-lg px-3 py-1 bg-gray-50/50">
                  Mais de <span className="text-green-600">10.000 empresas</span> confiam
                </p>
              </div>
            </div>

            <div className="relative lg:block hidden lg:flex items-center justify-end min-h-[500px]">
              <div className="absolute -top-20 -right-20 w-96 h-96 bg-green-100 rounded-full blur-3xl opacity-50 animate-pulse"></div>
              <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-green-200 rounded-full blur-3xl opacity-50 animate-pulse" style={{ animationDelay: '1s' }}></div>

              <div className="relative z-10 w-full max-w-lg transform hover:scale-[1.02] transition-all duration-500 ease-out animate-float">
                <Image
                  src="/image/home/phone1.webp"
                  alt="Plataforma de Gestão"
                  width={500}
                  height={650}
                  priority
                  className="w-full h-auto object-contain"
                  style={{
                    maxHeight: '650px',
                    minHeight: '400px',
                    filter: 'drop-shadow(0 30px 60px -12px rgba(34, 197, 94, 0.3))'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Company Form Section */}
      <section id="formularioAgendamento" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-6 tracking-tight">
              Comece aqui seu <span className="text-green-600">agendamento</span>
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Apenas insira as informações do empregador, seja um CNPJ ou CPF, depois disso, clique em iniciar agendamento.
            </p>
          </div>
          <div className="max-w-2xl mx-auto">
            <CompanyForm />
          </div>
        </div>
      </section>

      {/* Relacionamento Riscos e Exames */}
      <RiscosExamesSection />

      {/* Benefits Section */}
      <BenefitsSection />

      {/* Process Section */}
      <ProcessSection />

      {/* Clinicas Parceiras Section */}
      <ClinicasParceirasSection />

      {/* FAQ Section */}
      <FaqSection />

      {/* Recovery Modal */}
      <RecoveryModal
        isOpen={showRecoveryModal}
        onConfirm={handleConfirmRecovery}
        onCancel={handleCancelRecovery}
        orderDate={pendingOrder ? new Date(pendingOrder.created_at).toLocaleDateString('pt-BR') : ''}
        orderValue={pendingOrder ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(pendingOrder.valor) : ''}
      />
    </div>
  )
}
